/**
 * Client side of live collaboration for one open project.
 *
 * `confirmed` is the server's state at `seq`; `pending` are local transactions not yet in the
 * log. What the user sees is confirmed + pending. A local edit is applied immediately (no
 * waiting for the network), queued, and sent; the server's broadcast of it confirms it. A remote
 * transaction is applied to `confirmed`, then the pending ones are replayed on top; one that no
 * longer applies is dropped and reported.
 */
import { applyTransaction, CommandError, newId, type Project, type Transaction } from '@elec/domain';
import type { SyncEvent, TxRequest, TxResponse } from './protocol.ts';

export interface PendingTx {
  txId: string;
  baseSeq: number;
  tx: Transaction;
}

export interface SessionOptions {
  project: Project;
  seq: number;
  clientId?: string;
  /** Pending transactions restored from storage (offline edits from a previous session). */
  pending?: PendingTx[];
  /** Send one transaction to the server. Rejects (throws) when offline. */
  send(req: TxRequest): Promise<TxResponse>;
  /** The user's view changed because of a remote transaction or a rebase. */
  onView(project: Project): void;
  /** A local change could not be kept (someone else changed the same things). */
  onRejected?(tx: Transaction, message: string): void;
  /** Persist the pending queue (for offline work). */
  onPending?(pending: PendingTx[]): void;
  /** A gap was detected in the event stream: reload from `seq`. */
  onGap?(seq: number): void;
  /** Sending works (true) or fails (false: retried with backoff). */
  onNetwork?(ok: boolean): void;
  /** The server state moved on (`confirmed` at `seq`): keep a copy for opening offline. */
  onConfirmed?(project: Project, seq: number): void;
}

const tryApply = (p: Project, tx: Transaction): Project | null => {
  try {
    return applyTransaction(p, tx).project;
  } catch (e) {
    if (e instanceof CommandError) return null;
    throw e;
  }
};

export class SyncSession {
  readonly clientId: string;
  confirmed: Project;
  seq: number;
  pending: PendingTx[];
  private inFlight: string | null = null;
  private online = true;
  private retryMs = 1000;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly opts: SessionOptions;

  constructor(opts: SessionOptions) {
    this.opts = opts;
    this.clientId = opts.clientId ?? newId();
    this.confirmed = opts.project;
    this.seq = opts.seq;
    this.pending = opts.pending ?? [];
  }

  /** confirmed + pending (dropping pending that no longer apply). */
  view(): Project {
    let p = this.confirmed;
    const kept: PendingTx[] = [];
    for (const t of this.pending) {
      const next = tryApply(p, t.tx);
      if (next) {
        p = next;
        kept.push(t);
      } else this.opts.onRejected?.(t.tx, 'conflict');
    }
    if (kept.length !== this.pending.length) {
      this.pending = kept;
      this.opts.onPending?.(kept);
    }
    return p;
  }

  /** A transaction the user just made (already applied to their view). */
  local(tx: Transaction): void {
    this.pending.push({ txId: newId(), baseSeq: this.seq, tx });
    this.opts.onPending?.(this.pending);
    void this.pump();
  }

  /** An event from the project's stream. */
  remote(ev: SyncEvent): void {
    if (ev.type === 'reset') {
      this.confirmed = ev.project;
      this.seq = ev.seq;
      this.inFlight = null;
      this.opts.onConfirmed?.(this.confirmed, this.seq);
      this.opts.onView(this.view());
      void this.pump();
      return;
    }
    if (ev.type !== 'tx') return;
    if (ev.seq <= this.seq) return; // already have it (backlog overlap)
    if (ev.seq !== this.seq + 1) {
      this.opts.onGap?.(this.seq);
      return;
    }
    const mine = this.pending.findIndex((t) => t.txId === ev.txId);
    const next = tryApply(this.confirmed, ev.tx);
    if (!next) {
      // The server's state differs from ours: ask for a reload.
      this.opts.onGap?.(this.seq);
      return;
    }
    this.confirmed = next;
    this.seq = ev.seq;
    this.opts.onConfirmed?.(this.confirmed, this.seq);
    if (mine >= 0) {
      // Our own transaction coming back: confirmed now includes it.
      this.pending.splice(mine, 1);
      this.opts.onPending?.(this.pending);
      if (this.inFlight === ev.txId) this.inFlight = null;
      if (mine === 0 && !this.pending.length) {
        // Nothing to rebase: the view is unchanged.
      } else this.opts.onView(this.view());
      void this.pump();
      return;
    }
    this.opts.onView(this.view());
  }

  setOnline(online: boolean): void {
    this.online = online;
    if (online) {
      if (this.retryTimer) clearTimeout(this.retryTimer);
      this.retryTimer = null;
      this.retryMs = 1000;
      this.inFlight = null;
      void this.pump();
    }
  }

  /** Stop retrying (project closed). */
  dispose(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.online = false;
  }

  get synced(): boolean {
    return this.pending.length === 0;
  }

  /** Send the first pending transaction (one in flight at a time, in order). */
  private async pump(): Promise<void> {
    if (!this.online || this.inFlight || !this.pending.length) return;
    const t = this.pending[0]!;
    this.inFlight = t.txId;
    let res: TxResponse;
    try {
      res = await this.opts.send({ txId: t.txId, clientId: this.clientId, baseSeq: t.baseSeq, tx: t.tx });
    } catch {
      this.inFlight = null;
      this.online = false;
      this.opts.onNetwork?.(false);
      // Retry with backoff (also retried as soon as the event stream reconnects).
      if (!this.retryTimer)
        this.retryTimer = setTimeout(() => {
          this.retryTimer = null;
          this.online = true;
          void this.pump();
        }, this.retryMs);
      this.retryMs = Math.min(this.retryMs * 2, 30_000);
      return;
    }
    this.retryMs = 1000;
    this.opts.onNetwork?.(true);
    if (res.ok) return; // confirmed through the stream
    // Rejected: drop it and show the result without it.
    if (this.inFlight === t.txId) this.inFlight = null;
    const i = this.pending.findIndex((x) => x.txId === t.txId);
    if (i >= 0) {
      this.pending.splice(i, 1);
      this.opts.onPending?.(this.pending);
      this.opts.onRejected?.(t.tx, res.message);
      this.opts.onView(this.view());
    } // else: already dropped (and reported) by a rebase
    void this.pump();
  }
}
